// Alocação do time nos PIs e o custo que sai disso.
//
// A conta é sempre: custo mensal da pessoa × % alocado no PI. O custo tem
// vigência, então um reajuste em maio não mexe no que já foi apurado em março.
// O KRA do mês não entra no rateio — ele fica ao lado como conferência do que a
// operação orçou contra o que as pessoas custam de fato.
//
// Tela inteira é de admin: com custo por pessoa, um PI com uma pessoa a 100%
// entregaria o salário dela.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Check, ChevronDown, Copy, Loader2, Pencil, Plus,
  RefreshCw, Search, Trash2, UserPlus, X,
} from 'lucide-react';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, LabelList,
  ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { apiService } from '@/services/api';
import { useTheme } from '@/contexts/theme-context';
import { CaixaTooltip, CartaoGrafico, StatTile, TabelaEquivalente } from './carteira-ui';
import { obterCoresValor, obterVizTheme } from './carteira-viz';
import { moeda, moedaCurta, moedaExata } from './carteira-types';
import {
  CRITERIOS, CriterioRateio, CustoAcumulado, GradeCusto, LinhaPessoa, PoolCusto, VigenciaCusto,
  competenciaAtual, competenciaCurta, competenciasProximas, nomeCurtoDoPi,
} from './custo-types';

const classeCampo =
  'px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg ' +
  'bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white text-sm';

const classeRotulo =
  'block text-xs font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light mb-1';

/** Converte "1.234,56" ou "1234.56" em número. Vazio vira null. */
function parseValor(texto: string): number | null {
  const limpo = texto.replace(/[R$\s]/gi, '').trim();
  if (!limpo) return null;
  const normalizado = limpo.lastIndexOf(',') > limpo.lastIndexOf('.')
    ? limpo.replace(/\./g, '').replace(',', '.')
    : limpo.replace(/,/g, '');
  const n = parseFloat(normalizado);
  return Number.isFinite(n) ? n : null;
}

export function EquipeCarteiraPage() {
  const { theme } = useTheme();
  const cores = obterCoresValor(theme);
  const viz = obterVizTheme(theme);

  const [competencia, setCompetencia] = useState(competenciaAtual);
  const [grade, setGrade] = useState<GradeCusto | null>(null);
  const [acumulado, setAcumulado] = useState<CustoAcumulado | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [incluirInativos, setIncluirInativos] = useState(true);

  const [editandoCusto, setEditandoCusto] = useState<LinhaPessoa | null>(null);
  const [alocandoPara, setAlocandoPara] = useState<LinhaPessoa | null>(null);
  const [mostrarCopiar, setMostrarCopiar] = useState(false);
  const [mostrarNovaPessoa, setMostrarNovaPessoa] = useState(false);
  const [editandoPool, setEditandoPool] = useState<PoolCusto | 'novo' | null>(null);
  const [squadTabela, setSquadTabela] = useState('');
  const [soComEquipe, setSoComEquipe] = useState(false);
  const [editandoKra, setEditandoKra] = useState(false);
  const [kraTexto, setKraTexto] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const [resGrade, resAcum] = await Promise.all([
        apiService.obterGradeCustoCarteira(competencia, incluirInativos),
        apiService.obterCustoAcumuladoCarteira(),
      ]);
      if (!resGrade.success) throw new Error(resGrade.message || 'Falha ao carregar a grade');
      setGrade(resGrade.data as GradeCusto);
      setAcumulado(resAcum.success ? (resAcum.data as CustoAcumulado) : null);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível carregar a alocação');
    } finally {
      setCarregando(false);
    }
  }, [competencia, incluirInativos]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => { setKraTexto(grade?.kra ? String(grade.kra) : ''); }, [grade?.kra]);

  const totais = grade?.totais;
  const meses = useMemo(competenciasProximas, []);
  /** Pessoa-mês somado — a base do rateio "por cabeça" e do custo unitário. */
  const totalCabecas = (acumulado?.porPi ?? []).reduce((acc, p) => acc + p.cabecas, 0);
  /** Alguém tem custo mensal cadastrado? Decide quais cartões fazem sentido. */
  const usaCustoPorPessoa = (grade?.pessoas ?? []).some((p) => p.custoMensal !== null);

  /** Squads presentes nos projetos, para o filtro da tabela. */
  const squadsDosProjetos = useMemo(
    () => [...new Set((acumulado?.porPi ?? []).map((p) => p.squad).filter(Boolean))].sort() as string[],
    [acumulado]
  );

  const linhasProjetos = useMemo(
    () => (acumulado?.porPi ?? [])
      .filter((p) => (!squadTabela || p.squad === squadTabela) && (!soComEquipe || p.temEquipe)),
    [acumulado, squadTabela, soComEquipe]
  );

  /** Código do PI → nome do cliente, para a tabela não mostrar código cru. */
  const nomeDoPi = useMemo(
    () => new Map((acumulado?.porPi ?? []).map((p) => [p.piCodigo, nomeCurtoDoPi(p.descricao) ?? p.piCodigo])),
    [acumulado]
  );

  /** Quanto cada tipo de custo soma no total — a tabela lista mês a mês. */
  const totaisPorTipo = useMemo(() => {
    const mapa = new Map<string, { nome: string; total: number; meses: number }>();
    for (const r of acumulado?.rateios ?? []) {
      const atual = mapa.get(r.nome) ?? { nome: r.nome, total: 0, meses: 0 };
      atual.total += r.valor;
      atual.meses += 1;
      mapa.set(r.nome, atual);
    }
    return [...mapa.values()].sort((a, b) => b.total - a.total);
  }, [acumulado]);

  /** Margem por PI, da pior para a melhor — quem não se paga aparece primeiro. */
  const margens = useMemo(
    () => (acumulado?.porPi ?? [])
      // Só entra quem tem custo apurado: sem custo não há margem para comparar.
      .filter((p) => p.margem !== null && p.temEquipe)
      .map((p) => ({
        piCodigo: p.piCodigo,
        projeto: nomeCurtoDoPi(p.descricao),
        rotulo: nomeCurtoDoPi(p.descricao) ?? p.piCodigo,
        margem: p.margem as number,
        faturado: p.faturado ?? 0,
        custo: p.custoAcumulado,
        cabecas: p.cabecas,
      }))
      .sort((a, b) => a.margem - b.margem),
    [acumulado]
  );

  const salvarKra = async () => {
    const valor = parseValor(kraTexto);
    if (valor === null) { setErro('Informe o KRA do mês'); return; }
    setSalvando(true);
    const res = await apiService.definirKraCarteira({ competencia, valor });
    setSalvando(false);
    if (!res.success) { setErro(res.errors?.[0] || res.message || 'Falha ao salvar o KRA'); return; }
    setEditandoKra(false);
    carregar();
  };

  if (carregando && !grade) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-schmersal-blue" aria-hidden />
        <span className="sr-only">Carregando alocação</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-schmersal-gray-dark dark:text-white">
            Equipe e custo da carteira
          </h1>
          <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">
            Quem está em cada PI e quanto isso custa. O custo pode vir de dois lados:
            um total do período rateado entre os projetos, e/ou o custo mensal de cada
            pessoa vezes o percentual alocado.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={carregar}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${carregando ? 'animate-spin' : ''}`} aria-hidden />
            Atualizar
          </button>
          <button
            onClick={() => setMostrarCopiar(true)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark transition-colors"
          >
            <Copy className="h-4 w-4" aria-hidden />
            Copiar de outro mês
          </button>
          <button
            onClick={() => setMostrarNovaPessoa(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors"
          >
            <UserPlus className="h-4 w-4" aria-hidden />
            Nova pessoa
          </button>
        </div>
      </div>

      {erro && (
        <div className="flex items-start gap-3 rounded-lg border border-schmersal-yellow-dark/40 bg-schmersal-yellow/10 p-4">
          <AlertTriangle className="h-5 w-5 text-schmersal-yellow-dark shrink-0 mt-0.5" aria-hidden />
          <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light flex-1">{erro}</p>
          <button onClick={() => setErro(null)} aria-label="Fechar aviso"><X className="h-4 w-4 text-schmersal-gray-medium" aria-hidden /></button>
        </div>
      )}
      {aviso && (
        <div className="flex items-start gap-3 rounded-lg border border-rg-line dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darker p-4">
          <Check className="h-5 w-5 text-schmersal-blue shrink-0 mt-0.5" aria-hidden />
          <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light flex-1">{aviso}</p>
          <button onClick={() => setAviso(null)} aria-label="Fechar aviso"><X className="h-4 w-4 text-schmersal-gray-medium" aria-hidden /></button>
        </div>
      )}

      {/* ------------------------------------------------------ mês e KRA */}
      <div className="flex flex-wrap items-end gap-4 bg-white dark:bg-schmersal-gray-darker rounded-lg shadow p-4 border border-rg-line dark:border-schmersal-gray-dark">
        <div>
          <label htmlFor="c-mes" className={classeRotulo}>Mês</label>
          <select id="c-mes" className={classeCampo} value={competencia} onChange={(e) => setCompetencia(e.target.value)}>
            {meses.map((m) => <option key={m} value={m}>{competenciaCurta(m)}</option>)}
          </select>
        </div>

        <div>
          <span className={classeRotulo}>KRA do mês</span>
          {editandoKra ? (
            <span className="flex items-center gap-2">
              <input
                autoFocus
                className={`${classeCampo} w-40`}
                value={kraTexto}
                onChange={(e) => setKraTexto(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') salvarKra(); if (e.key === 'Escape') setEditandoKra(false); }}
                placeholder="1.750.000"
              />
              <button onClick={salvarKra} disabled={salvando} className="p-2 rounded-lg bg-schmersal-blue text-white disabled:opacity-50" aria-label="Salvar KRA">
                <Check className="h-4 w-4" aria-hidden />
              </button>
              <button onClick={() => setEditandoKra(false)} className="p-2 rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark" aria-label="Cancelar">
                <X className="h-4 w-4 text-schmersal-gray-medium" aria-hidden />
              </button>
            </span>
          ) : (
            <button
              onClick={() => setEditandoKra(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark text-sm text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark"
            >
              {grade?.kra ? moeda(grade.kra) : 'não informado'}
              <Pencil className="h-3.5 w-3.5 text-schmersal-gray-medium" aria-hidden />
            </button>
          )}
        </div>

        <label className="inline-flex items-center gap-2 text-sm text-schmersal-gray-dark dark:text-white pb-2 cursor-pointer">
          <input
            type="checkbox"
            checked={incluirInativos}
            onChange={(e) => setIncluirInativos(e.target.checked)}
            className="rounded border-schmersal-gray-light"
          />
          Mostrar quem ainda não tem acesso
        </label>
      </div>

      {/* ------------------------------------------------------------- KPIs
          Os cartões acompanham o modo em uso: sem custo por pessoa cadastrado,
          mostrar "R$ 0 · 23 ainda sem custo" seria uma pendência inventada. */}
      {totais && (
        <div className={`grid grid-cols-2 md:grid-cols-3 gap-4 ${usaCustoPorPessoa ? 'xl:grid-cols-5' : 'xl:grid-cols-4'}`}>
          <StatTile
            rotulo="Custo acumulado"
            valor={moedaCurta(acumulado?.totais.custoTotal ?? 0)}
            detalhe={`${acumulado?.totais.meses ?? 0} ${acumulado?.totais.meses === 1 ? 'mês lançado' : 'meses lançados'} · ${totalCabecas.toFixed(0)} pessoa-mês`}
          />
          <StatTile
            rotulo="Rateado"
            valor={moedaCurta(acumulado?.totais.custoRateado ?? 0)}
            detalhe={`${acumulado?.rateios.length ?? 0} custo(s) do período distribuídos entre os PIs`}
          />
          <StatTile
            rotulo="Custo por pessoa-mês"
            valor={totalCabecas > 0 ? moedaCurta((acumulado?.totais.custoTotal ?? 0) / totalCabecas) : '—'}
            detalhe="Quanto custa manter uma pessoa um mês num projeto"
          />
          {usaCustoPorPessoa ? (
            <>
              <StatTile
                rotulo="Custo do time no mês"
                valor={moedaCurta(totais.custoTotalPessoas)}
                detalhe={totais.semCusto > 0
                  ? `${totais.pessoas} pessoas · ${totais.semCusto} ainda sem custo`
                  : `${totais.pessoas} pessoas, todas com custo`}
              />
              <StatTile
                rotulo="Não alocado no mês"
                valor={moedaCurta(totais.custoNaoAlocado)}
                tom="alerta"
                detalhe="Custo por pessoa que não caiu em nenhum PI"
              />
            </>
          ) : (
            <StatTile
              rotulo="Pessoas alocadas"
              valor={String(grade?.pessoas.filter((p) => p.alocacoes.length > 0).length ?? 0)}
              detalhe={`de ${totais.pessoas} cadastradas, em ${competenciaCurta(competencia)}`}
            />
          )}
        </div>
      )}

      {acumulado && acumulado.totais.lacunas.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-schmersal-yellow-dark/40 bg-schmersal-yellow/10 p-4">
          <AlertTriangle className="h-5 w-5 text-schmersal-yellow-dark shrink-0 mt-0.5" aria-hidden />
          <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light">
            Falta lançar custo em meses que já têm gente alocada, então o total está por baixo:{' '}
            {acumulado.totais.lacunas.map((l) => (
              <span key={l.nome} className="inline">
                <strong>{l.nome}</strong> em {l.meses.map(competenciaCurta).join(', ')}
              </span>
            )).reduce((acc: React.ReactNode[], el, i) => (i === 0 ? [el] : [...acc, '; ', el]), [])}.
          </p>
        </div>
      )}

      {acumulado && acumulado.totais.pessoasSemCusto.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-schmersal-yellow-dark/40 bg-schmersal-yellow/10 p-4">
          <AlertTriangle className="h-5 w-5 text-schmersal-yellow-dark shrink-0 mt-0.5" aria-hidden />
          <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light">
            O acumulado está subestimado: {acumulado.totais.pessoasSemCusto.join(', ')}{' '}
            {acumulado.totais.pessoasSemCusto.length === 1 ? 'está alocada' : 'estão alocadas'} em
            projeto mas ainda sem custo cadastrado, então {acumulado.totais.pessoasSemCusto.length === 1 ? 'entra' : 'entram'} como zero.
          </p>
        </div>
      )}

      {/* --------------------------------------------------------- a grade */}
      <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow border border-rg-line dark:border-schmersal-gray-dark overflow-hidden">
        <div className="p-4 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
          <h2 className="font-semibold text-schmersal-gray-dark dark:text-white">
            Alocação de {competenciaCurta(competencia)}
          </h2>
          <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-0.5">
            {totais?.sobreAlocadas
              ? `${totais.sobreAlocadas} pessoa(s) passam de 100% no mês — a soma é mostrada, não bloqueada.`
              : 'Clique no valor para editar o custo, ou em “alocar” para pôr a pessoa num PI.'}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-schmersal-gray-light/40 dark:bg-schmersal-gray-dark/40">
              <tr className="text-left">
                <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light">Pessoa</th>
                <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light text-right w-[150px]">Custo/mês</th>
                <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light">Alocação nos PIs</th>
                <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light text-right w-[110px]">Total</th>
                <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light text-right w-[140px]">Não alocado</th>
              </tr>
            </thead>
            <tbody>
              {(grade?.pessoas ?? []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-schmersal-gray-medium dark:text-schmersal-gray-light">
                  Nenhuma pessoa cadastrada.
                </td></tr>
              )}
              {(grade?.pessoas ?? []).map((p) => (
                <tr key={p.userId} className="border-t border-schmersal-gray-light dark:border-schmersal-gray-dark align-top">
                  <td className="px-4 py-3">
                    <span className="font-medium text-schmersal-gray-dark dark:text-white">{p.nome}</span>
                    {!p.ativo && (
                      <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded border border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-gray-medium">
                        sem acesso
                      </span>
                    )}
                    <span className="block text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                      {[p.squad, p.cargo].filter(Boolean).join(' · ') || p.email}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setEditandoCusto(p)}
                      className={`inline-flex items-center gap-1.5 tabular-nums hover:underline ${
                        p.custoMensal === null
                          ? 'text-schmersal-gray-medium'
                          : 'text-schmersal-gray-dark dark:text-white'
                      }`}
                    >
                      {p.custoMensal === null ? 'definir' : moeda(p.custoMensal)}
                      <Pencil className="h-3 w-3 text-schmersal-gray-medium" aria-hidden />
                    </button>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {p.alocacoes.map((a) => (
                        <span
                          key={a.id}
                          title={[a.descricao, a.custo === null ? null : moedaExata(a.custo)].filter(Boolean).join(' — ')}
                          className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs text-schmersal-gray-dark dark:text-white"
                          style={{ borderColor: cores.realizado, background: `${cores.realizado}14` }}
                        >
                          <span className="font-medium">{a.piCodigo}</span>
                          <span className="tabular-nums">{a.percentual}%</span>
                          {a.custo !== null && (
                            <span className="tabular-nums text-schmersal-gray-medium dark:text-schmersal-gray-light">
                              {moeda(a.custo)}
                            </span>
                          )}
                        </span>
                      ))}
                      <button
                        onClick={() => setAlocandoPara(p)}
                        className="inline-flex items-center gap-1 rounded-full border border-dashed border-schmersal-gray-light dark:border-schmersal-gray-dark px-2 py-0.5 text-xs text-schmersal-gray-medium hover:text-schmersal-blue dark:hover:text-white"
                      >
                        <Plus className="h-3 w-3" aria-hidden />
                        alocar
                      </button>
                    </div>
                  </td>

                  <td className="px-4 py-3 text-right tabular-nums">
                    <span className={p.somaPercentual > 100
                      ? 'font-semibold text-schmersal-yellow-dark'
                      : 'text-schmersal-gray-dark dark:text-white'}>
                      {p.somaPercentual}%
                    </span>
                    {p.somaPercentual > 100 && (
                      <span className="block text-[11px] text-schmersal-gray-medium">sobre-alocada</span>
                    )}
                  </td>

                  <td className="px-4 py-3 text-right tabular-nums text-schmersal-gray-dark dark:text-schmersal-gray-light">
                    {p.custoNaoAlocado === null ? '—' : moeda(p.custoNaoAlocado)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --------------------------------------------------------- gráficos */}
      {acumulado && acumulado.porPi.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2">
            <CartaoGrafico
              titulo="Cada projeto se paga?"
              subtitulo={`Faturado menos custo, de ${competenciaCurta(acumulado.totais.janela.inicio)} a ${competenciaCurta(acumulado.totais.janela.fim)} — a janela em que os dois existem.${
                acumulado.totais.janela.mesesSemFaturado
                  ? ` ${acumulado.totais.janela.mesesSemFaturado} mês(es) de custo ficam de fora, por ainda não terem faturamento fechado.`
                  : ''
              }`}
            >
              <ResponsiveContainer width="100%" height={Math.max(240, margens.length * 40 + 40)}>
                <BarChart data={margens} layout="vertical" margin={{ top: 4, right: 92, left: 8, bottom: 4 }}>
                  <CartesianGrid stroke={viz.grade} strokeDasharray="3 3" horizontal={false} />
                  {/* Folga no domínio: sem ela a barra mais longa encosta na borda
                      e o rótulo dela cai por cima dos nomes do eixo. */}
                  <XAxis
                    type="number"
                    domain={[
                      (min: number) => (min < 0 ? min * 1.3 : 0),
                      (max: number) => (max > 0 ? max * 1.35 : 0),
                    ]}
                    tickFormatter={(v: number) => moedaCurta(v)}
                    tick={{ fill: viz.textoSecundario, fontSize: 12 }}
                    axisLine={false} tickLine={false} />
                  {/* O eixo traz o cliente, não o código: ninguém pensa em "PI_1153". */}
                  <YAxis type="category" dataKey="rotulo"
                    tick={{ fill: viz.textoSecundario, fontSize: 12 }}
                    axisLine={false} tickLine={false} width={124} />
                  {/* O zero é a leitura inteira do gráfico: à esquerda não se paga. */}
                  <ReferenceLine x={0} stroke={viz.eixo} strokeWidth={2} />
                  <Tooltip
                    cursor={{ fill: viz.grade, fillOpacity: 0.35 }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload as (typeof margens)[number];
                      return (
                        <CaixaTooltip
                          titulo={`${p.piCodigo}${p.projeto ? ` — ${p.projeto}` : ''}`}
                          linhas={[
                            {
                              nome: p.margem >= 0 ? 'sobra' : 'falta',
                              valor: moedaExata(Math.abs(p.margem)),
                              cor: p.margem >= 0 ? viz.divergentePositivo : viz.divergenteNegativo,
                            },
                            { nome: 'faturado', valor: moedaExata(p.faturado) },
                            { nome: 'custo', valor: moedaExata(p.custo) },
                            { nome: 'pessoa-mês', valor: p.cabecas.toFixed(0) },
                          ]}
                        />
                      );
                    }}
                  />
                  <Bar dataKey="margem" radius={4} maxBarSize={26}>
                    {margens.map((m) => (
                      <Cell key={m.piCodigo}
                        fill={m.margem >= 0 ? viz.divergentePositivo : viz.divergenteNegativo} />
                    ))}
                    <LabelList
                      dataKey="margem"
                      position="right"
                      formatter={(v: unknown) => moedaCurta(Number(v))}
                      style={{ fill: viz.textoSecundario, fontSize: 11 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <TabelaEquivalente
                colunas={['Projeto', 'Faturado', 'Custo', 'Margem']}
                linhas={margens.map((m) => [
                  `${m.piCodigo}${m.projeto ? ` (${m.projeto})` : ''}`, m.faturado, m.custo, m.margem,
                ])}
              />
            </CartaoGrafico>
          </div>

          <CartaoGrafico
            titulo="Custo acumulado"
            subtitulo="Quanto o time já consumiu, mês a mês."
          >
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={acumulado.serieMensal} margin={{ top: 8, right: 8, left: 8, bottom: 4 }}>
                <defs>
                  <linearGradient id="grad-custo" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={cores.realizado} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={cores.realizado} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={viz.grade} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="competencia" tickFormatter={competenciaCurta}
                  tick={{ fill: viz.textoSecundario, fontSize: 12 }}
                  axisLine={{ stroke: viz.eixo }} tickLine={false} />
                <YAxis tickFormatter={(v: number) => moedaCurta(v)}
                  tick={{ fill: viz.textoSecundario, fontSize: 12 }}
                  axisLine={false} tickLine={false} width={72} />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const p = payload[0].payload as CustoAcumulado['serieMensal'][number];
                    return (
                      <CaixaTooltip
                        titulo={competenciaCurta(p.competencia)}
                        linhas={[
                          { nome: 'acumulado', valor: moedaExata(p.acumulado), cor: cores.realizado },
                          { nome: 'no mês', valor: moedaExata(p.custo) },
                          { nome: 'pessoa-mês', valor: p.cabecas.toFixed(0) },
                          { nome: p.pis === 1 ? 'PI' : 'PIs', valor: String(p.pis) },
                        ]}
                      />
                    );
                  }}
                />
                {/* Uma série só: o título já diz o que é, então dispensa legenda. */}
                <Area type="monotone" dataKey="acumulado" stroke={cores.realizado} strokeWidth={2}
                  fill="url(#grad-custo)" />
              </AreaChart>
            </ResponsiveContainer>
            <TabelaEquivalente
              colunas={['Mês', 'No mês', 'Acumulado']}
              linhas={acumulado.serieMensal.map((m) => [competenciaCurta(m.competencia), m.custo, m.acumulado])}
            />
          </CartaoGrafico>
        </div>
      )}

      {/* ------------------------------------------------- custos rateados */}
      <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow border border-rg-line dark:border-schmersal-gray-dark overflow-hidden">
        <div className="flex flex-wrap items-start justify-between gap-3 p-4 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
          <div>
            <h2 className="font-semibold text-schmersal-gray-dark dark:text-white">Custos rateados</h2>
            <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-0.5">
              Um total conhecido do período (material, KRA…) que desce para os PIs pelo
              critério escolhido. Some com o custo por pessoa, quando houver os dois.
            </p>
            {totaisPorTipo.length > 0 && (
              <p className="text-sm text-schmersal-gray-dark dark:text-white mt-2 flex flex-wrap gap-x-4">
                {totaisPorTipo.map((t) => (
                  <span key={t.nome}>
                    <strong>{t.nome}</strong> {moeda(t.total)}{' '}
                    <span className="text-schmersal-gray-medium dark:text-schmersal-gray-light">
                      em {t.meses} {t.meses === 1 ? 'mês' : 'meses'}
                    </span>
                  </span>
                ))}
              </p>
            )}
          </div>
          <button
            onClick={() => setEditandoPool('novo')}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark transition-colors"
          >
            <Plus className="h-4 w-4" aria-hidden />
            Novo custo
          </button>
        </div>

        {(acumulado?.rateios ?? []).length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
            Nenhum custo rateado cadastrado.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-schmersal-gray-light/40 dark:bg-schmersal-gray-dark/40">
                <tr className="text-left">
                  <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light">Custo</th>
                  <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light">Período</th>
                  <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light">Critério</th>
                  <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light">Projetos</th>
                  <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light text-right">Valor</th>
                  <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light text-right">Distribuído</th>
                  <th className="px-4 py-2.5 w-10" />
                </tr>
              </thead>
              <tbody>
                {(acumulado?.rateios ?? []).map((r) => (
                  <tr key={r.id} className="border-t border-schmersal-gray-light dark:border-schmersal-gray-dark">
                    <td className="px-4 py-2.5">
                      <span className="font-medium text-schmersal-gray-dark dark:text-white">{r.nome}</span>
                      {r.alerta && (
                        <span className="block text-xs text-schmersal-yellow-dark">{r.alerta}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-schmersal-gray-dark dark:text-schmersal-gray-light">
                      {r.competenciaInicio === r.competenciaFim
                        ? competenciaCurta(r.competenciaInicio)
                        : `${competenciaCurta(r.competenciaInicio)} – ${competenciaCurta(r.competenciaFim)}`}
                    </td>
                    <td className="px-4 py-2.5 text-schmersal-gray-dark dark:text-schmersal-gray-light">
                      {CRITERIOS.find((c) => c.id === r.criterio)?.rotulo ?? r.criterio}
                    </td>
                    <td
                      className="px-4 py-2.5 w-[180px] max-w-[180px] text-schmersal-gray-dark dark:text-schmersal-gray-light"
                      title={r.piCodigos.length ? r.piCodigos.join(', ') : undefined}
                    >
                      <span className="block truncate">
                        {r.piCodigos.length === 0
                          ? <span className="text-schmersal-gray-medium">todos com alocação</span>
                          : r.piCodigos.map((c) => nomeDoPi.get(c) ?? c).join(', ')}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap text-schmersal-gray-dark dark:text-white">
                      {moeda(r.valor)}
                    </td>
                    {/* Distribuído menor que o valor significa que sobrou dinheiro
                        sem projeto — o número aparece para não passar batido. */}
                    <td className={`px-4 py-2.5 text-right tabular-nums whitespace-nowrap ${
                      Math.abs(r.distribuido - r.valor) > 0.01
                        ? 'text-schmersal-yellow-dark font-medium'
                        : 'text-schmersal-gray-dark dark:text-schmersal-gray-light'
                    }`}>
                      {moeda(r.distribuido)}
                    </td>
                    <td className="px-4 py-2.5">
                      <button
                        onClick={() => setEditandoPool(r)}
                        aria-label={`Editar ${r.nome}`}
                        className="p-1 rounded hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark"
                      >
                        <Pencil className="h-4 w-4 text-schmersal-gray-medium" aria-hidden />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ------------------------------------------ custo acumulado por PI */}
      {acumulado && acumulado.porPi.length > 0 && (
        <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow border border-rg-line dark:border-schmersal-gray-dark overflow-hidden">
          <div className="flex flex-wrap items-end justify-between gap-3 p-4 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
            <div>
              <h2 className="font-semibold text-schmersal-gray-dark dark:text-white">Saúde dos projetos</h2>
              <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-0.5">
                Todos os projetos ativos que ainda têm saldo ou já faturaram — inclusive os
                que estão sem ninguém apontado.
                {acumulado.totais.semEquipe > 0 && (
                  <> <strong className="text-schmersal-gray-dark dark:text-white">
                    {acumulado.totais.semEquipe}
                  </strong> estão sem equipe.</>
                )}
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label htmlFor="t-squad" className={classeRotulo}>Squad</label>
                <select id="t-squad" className={classeCampo} value={squadTabela} onChange={(e) => setSquadTabela(e.target.value)}>
                  <option value="">Todos</option>
                  {squadsDosProjetos.map((sq) => <option key={sq} value={sq}>{sq}</option>)}
                </select>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-schmersal-gray-dark dark:text-white pb-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={soComEquipe}
                  onChange={(e) => setSoComEquipe(e.target.checked)}
                  className="rounded border-schmersal-gray-light"
                />
                Só com equipe apontada
              </label>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-schmersal-gray-light/40 dark:bg-schmersal-gray-dark/40">
                <tr className="text-left">
                  <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light">PI</th>
                  <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light">Squad</th>
                  <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light text-right" title="Soma dos percentuais de alocação ao longo dos meses">Pessoa-mês</th>
                  <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light text-right">Custo acumulado</th>
                  <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light text-right" title="Só meses fechados">Faturado</th>
                  <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light text-right" title="Saldo a executar no retrato mais recente">Carteira</th>
                  <th className="px-4 py-2.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light text-right">Margem</th>
                </tr>
              </thead>
              <tbody>
                {linhasProjetos.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-schmersal-gray-medium dark:text-schmersal-gray-light">
                    Nenhum projeto com esses filtros.
                  </td></tr>
                )}
                {linhasProjetos.map((pi) => (
                  <tr key={pi.piCodigo} className="border-t border-schmersal-gray-light dark:border-schmersal-gray-dark">
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-2">
                        <span className="font-medium text-schmersal-gray-dark dark:text-white">{pi.piCodigo}</span>
                        {/* O badge vinha da coluna Pessoas, que saiu para não expor quem
                            está em qual projeto. Sem ele, PI sem equipe só se denunciava
                            pelos traços em custo e margem. */}
                        {!pi.temEquipe && (
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-schmersal-gray-light dark:border-schmersal-gray-dark px-2 py-0.5 text-[11px] text-schmersal-gray-medium">
                            sem equipe
                          </span>
                        )}
                      </span>
                      {pi.descricao && (
                        <span className="block text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light truncate max-w-[260px]">
                          {pi.descricao}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-schmersal-gray-dark dark:text-schmersal-gray-light">{pi.squad ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-schmersal-gray-dark dark:text-schmersal-gray-light">
                      {pi.cabecas > 0 ? pi.cabecas.toFixed(pi.cabecas % 1 === 0 ? 0 : 1) : '—'}
                    </td>
                    <td
                      className="px-4 py-2.5 text-right tabular-nums font-medium whitespace-nowrap text-schmersal-gray-dark dark:text-white"
                      title={`rateado ${moedaExata(pi.custoRateado)} + direto ${moedaExata(pi.custoDireto)}`}
                    >
                      {pi.temEquipe ? moeda(pi.custoAcumulado) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap text-schmersal-gray-dark dark:text-schmersal-gray-light">
                      {pi.faturado === null ? '—' : moeda(pi.faturado)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap text-schmersal-gray-dark dark:text-schmersal-gray-light">
                      {pi.saldo === null ? '—' : moeda(pi.saldo)}
                    </td>
                    {/* Sem custo apurado não há margem — mostrar "faturado − 0" faria
                        um projeto sem equipe parecer o mais lucrativo da lista. */}
                    <td
                      className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap font-medium text-schmersal-gray-dark dark:text-white"
                      title={pi.margem === null ? 'Sem custo apurado neste projeto' : undefined}
                    >
                      {pi.margem === null ? '—' : moeda(pi.margem)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editandoCusto && (
        <ModalCusto
          pessoa={editandoCusto}
          competencia={competencia}
          onFechar={() => setEditandoCusto(null)}
          onSalvo={(msg) => { setEditandoCusto(null); setAviso(msg); carregar(); }}
        />
      )}

      {alocandoPara && (
        <ModalAlocacao
          pessoa={alocandoPara}
          competencia={competencia}
          onFechar={() => setAlocandoPara(null)}
          onSalvo={(msg) => { setAlocandoPara(null); setAviso(msg); carregar(); }}
        />
      )}

      {mostrarCopiar && (
        <ModalCopiar
          destino={competencia}
          onFechar={() => setMostrarCopiar(false)}
          onSalvo={(msg) => { setMostrarCopiar(false); setAviso(msg); carregar(); }}
        />
      )}

      {editandoPool && (
        <ModalPool
          pool={editandoPool === 'novo' ? null : editandoPool}
          projetosComAlocacao={(acumulado?.porPi ?? []).map((p) => ({
            codigo: p.piCodigo,
            nome: nomeCurtoDoPi(p.descricao) ?? p.piCodigo,
            cabecas: p.cabecas,
          }))}
          onFechar={() => setEditandoPool(null)}
          onSalvo={(msg: string) => { setEditandoPool(null); setAviso(msg); carregar(); }}
        />
      )}

      {mostrarNovaPessoa && (
        <ModalNovaPessoa
          onFechar={() => setMostrarNovaPessoa(false)}
          onSalvo={(msg) => { setMostrarNovaPessoa(false); setAviso(msg); carregar(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- modais

function Moldura({ titulo, subtitulo, onFechar, children }: {
  titulo: string; subtitulo?: string; onFechar: () => void; children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onFechar}>
      <div
        className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
          <div>
            <h2 className="font-semibold text-schmersal-gray-dark dark:text-white">{titulo}</h2>
            {subtitulo && <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-0.5">{subtitulo}</p>}
          </div>
          <button onClick={onFechar} aria-label="Fechar" className="p-1 rounded hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark shrink-0">
            <X className="h-5 w-5 text-schmersal-gray-medium" aria-hidden />
          </button>
        </div>
        <div className="p-5 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function Erro({ texto }: { texto: string | null }) {
  if (!texto) return null;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-schmersal-yellow-dark/40 bg-schmersal-yellow/10 p-3">
      <AlertTriangle className="h-4 w-4 text-schmersal-yellow-dark shrink-0 mt-0.5" aria-hidden />
      <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light">{texto}</p>
    </div>
  );
}

/** Custo mensal com vigência + histórico das vigências anteriores. */
function ModalCusto({ pessoa, competencia, onFechar, onSalvo }: {
  pessoa: LinhaPessoa; competencia: string; onFechar: () => void; onSalvo: (msg: string) => void;
}) {
  const [valor, setValor] = useState(pessoa.custoMensal === null ? '' : String(pessoa.custoMensal));
  const [vigencia, setVigencia] = useState(competencia);
  const [historico, setHistorico] = useState<VigenciaCusto[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const meses = useMemo(competenciasProximas, []);

  const recarregar = useCallback(async () => {
    const res = await apiService.obterHistoricoCustoPessoaCarteira(pessoa.userId);
    if (res.success) setHistorico(res.data as VigenciaCusto[]);
  }, [pessoa.userId]);

  useEffect(() => { recarregar(); }, [recarregar]);

  const salvar = async () => {
    const custo = parseValor(valor);
    if (custo === null) { setErro('Informe o custo mensal'); return; }
    setSalvando(true);
    const res = await apiService.definirCustoPessoaCarteira(pessoa.userId, {
      vigenciaInicio: vigencia,
      custoMensal: custo,
    });
    setSalvando(false);
    if (!res.success) { setErro(res.errors?.[0] || res.message || 'Falha ao salvar'); return; }
    onSalvo(`Custo de ${pessoa.nome} definido a partir de ${competenciaCurta(vigencia)}.`);
  };

  const remover = async (id: string) => {
    const res = await apiService.removerVigenciaCustoCarteira(id);
    if (!res.success) { setErro(res.message || 'Falha ao remover'); return; }
    recarregar();
  };

  return (
    <Moldura titulo={`Custo de ${pessoa.nome}`} subtitulo="O valor vale deste mês em diante, até a próxima vigência." onFechar={onFechar}>
      <div className="flex gap-3">
        <div className="flex-1">
          <label htmlFor="m-valor" className={classeRotulo}>Custo mensal</label>
          <input
            id="m-valor" autoFocus className={`${classeCampo} w-full`} value={valor}
            onChange={(e) => setValor(e.target.value)} placeholder="12.000,00"
            onKeyDown={(e) => { if (e.key === 'Enter') salvar(); }}
          />
        </div>
        <div>
          <label htmlFor="m-vig" className={classeRotulo}>A partir de</label>
          <select id="m-vig" className={classeCampo} value={vigencia} onChange={(e) => setVigencia(e.target.value)}>
            {meses.map((m) => <option key={m} value={m}>{competenciaCurta(m)}</option>)}
          </select>
        </div>
      </div>

      <Erro texto={erro} />

      <button
        onClick={salvar} disabled={salvando}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors disabled:opacity-50"
      >
        {salvando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Check className="h-4 w-4" aria-hidden />}
        Salvar
      </button>

      {historico.length > 0 && (
        <div className="pt-3 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark">
          <p className="text-xs font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light uppercase tracking-wide mb-2">
            Vigências
          </p>
          <ul className="space-y-1.5">
            {historico.map((h) => (
              <li key={h.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-schmersal-gray-dark dark:text-white">
                  a partir de {competenciaCurta(h.vigenciaInicio)}
                </span>
                <span className="flex items-center gap-3">
                  <span className="tabular-nums text-schmersal-gray-dark dark:text-white">{moedaExata(h.custoMensal)}</span>
                  <button onClick={() => remover(h.id)} aria-label={`Remover vigência de ${h.vigenciaInicio}`} className="p-1 rounded hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark">
                    <Trash2 className="h-4 w-4 text-schmersal-gray-medium" aria-hidden />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Moldura>
  );
}

/** Aloca a pessoa num PI, com busca sobre o retrato atual da carteira. */
function ModalAlocacao({ pessoa, competencia, onFechar, onSalvo }: {
  pessoa: LinhaPessoa; competencia: string; onFechar: () => void; onSalvo: (msg: string) => void;
}) {
  const [busca, setBusca] = useState('');
  const [opcoes, setOpcoes] = useState<{ codigo: string; descricao: string | null; squad: string | null }[]>([]);
  const [piCodigo, setPiCodigo] = useState('');
  const [pct, setPct] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const restante = Math.max(0, 100 - pessoa.somaPercentual);

  useEffect(() => {
    const t = setTimeout(async () => {
      const res = await apiService.listarPisCarteira({ busca: busca || undefined, apenasComSaldo: true, limite: 25 });
      if (res.success) {
        const lista = (res.data as { pis: { codigo: string; descricao: string | null; squad: string | null }[] }).pis;
        setOpcoes(lista);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [busca]);

  const salvar = async () => {
    const percentualNum = parseValor(pct);
    if (!piCodigo) { setErro('Escolha o PI'); return; }
    if (percentualNum === null) { setErro('Informe o percentual'); return; }
    setSalvando(true);
    const res = await apiService.definirAlocacaoCarteira({
      competencia, userId: pessoa.userId, piCodigo, percentual: percentualNum,
    });
    setSalvando(false);
    if (!res.success) { setErro(res.errors?.[0] || res.message || 'Falha ao alocar'); return; }
    onSalvo(percentualNum === 0
      ? `${pessoa.nome} saiu de ${piCodigo} em ${competenciaCurta(competencia)}.`
      : `${pessoa.nome} alocada em ${piCodigo} (${percentualNum}%) em ${competenciaCurta(competencia)}.`);
  };

  return (
    <Moldura
      titulo={`Alocar ${pessoa.nome}`}
      subtitulo={`${competenciaCurta(competencia)} · ${pessoa.somaPercentual}% já alocado, ${restante}% livre`}
      onFechar={onFechar}
    >
      <div>
        <label htmlFor="a-busca" className={classeRotulo}>PI</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-schmersal-gray-medium" aria-hidden />
          <input
            id="a-busca" autoFocus className={`${classeCampo} w-full pl-9`} value={busca}
            onChange={(e) => setBusca(e.target.value)} placeholder="código ou cliente"
          />
        </div>
        <div className="mt-2 max-h-48 overflow-y-auto border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg divide-y divide-rg-hairline dark:divide-schmersal-gray-dark">
          {opcoes.length === 0 && (
            <p className="px-3 py-4 text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
              Nenhum PI com saldo bate com a busca.
            </p>
          )}
          {opcoes.map((o) => (
            <button
              key={o.codigo}
              onClick={() => setPiCodigo(o.codigo)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-schmersal-gray-light/50 dark:hover:bg-schmersal-gray-dark/50 ${
                piCodigo === o.codigo ? 'bg-schmersal-gray-light/60 dark:bg-schmersal-gray-dark/60' : ''
              }`}
            >
              <span className="font-medium text-schmersal-gray-dark dark:text-white">{o.codigo}</span>
              {piCodigo === o.codigo && <Check className="inline h-3.5 w-3.5 ml-2 text-schmersal-blue dark:text-schmersal-turquoise" aria-hidden />}
              <span className="block text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light truncate">
                {[o.squad, o.descricao].filter(Boolean).join(' · ')}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label htmlFor="a-pct" className={classeRotulo}>
          Percentual do mês <span className="font-normal">(0 remove a alocação)</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            id="a-pct" className={`${classeCampo} w-28`} value={pct}
            onChange={(e) => setPct(e.target.value)} placeholder="50"
            onKeyDown={(e) => { if (e.key === 'Enter') salvar(); }}
          />
          {restante > 0 && (
            <button onClick={() => setPct(String(restante))} className="text-sm text-schmersal-blue dark:text-schmersal-turquoise hover:underline">
              usar os {restante}% livres
            </button>
          )}
        </div>
        {pessoa.custoMensal !== null && parseValor(pct) !== null && (
          <p className="mt-1 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
            Custa {moedaExata(pessoa.custoMensal * ((parseValor(pct) ?? 0) / 100))} neste mês.
          </p>
        )}
      </div>

      <Erro texto={erro} />

      <button
        onClick={salvar} disabled={salvando}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors disabled:opacity-50"
      >
        {salvando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Check className="h-4 w-4" aria-hidden />}
        Salvar alocação
      </button>
    </Moldura>
  );
}

/** Copia a alocação de um mês para outro — o caminho normal de uso mês a mês. */
function ModalCopiar({ destino, onFechar, onSalvo }: {
  destino: string; onFechar: () => void; onSalvo: (msg: string) => void;
}) {
  const [origem, setOrigem] = useState('');
  const [disponiveis, setDisponiveis] = useState<string[]>([]);
  const [substituir, setSubstituir] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const res = await apiService.listarCompetenciasCarteira();
      if (res.success) {
        const lista = (res.data as string[]).filter((m) => m !== destino);
        setDisponiveis(lista);
        setOrigem(lista[0] ?? '');
      }
    })();
  }, [destino]);

  const copiar = async () => {
    if (!origem) { setErro('Escolha o mês de origem'); return; }
    setSalvando(true);
    const res = await apiService.copiarAlocacaoCarteira({ de: origem, para: destino, substituir });
    setSalvando(false);
    if (!res.success) { setErro(res.errors?.[0] || res.message || 'Falha ao copiar'); return; }
    onSalvo(res.message ?? 'Alocação copiada.');
  };

  return (
    <Moldura titulo={`Copiar alocação para ${competenciaCurta(destino)}`} onFechar={onFechar}>
      {disponiveis.length === 0 ? (
        <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
          Nenhum outro mês tem alocação lançada ainda.
        </p>
      ) : (
        <>
          <div>
            <label htmlFor="cp-de" className={classeRotulo}>Copiar de</label>
            <select id="cp-de" className={`${classeCampo} w-full`} value={origem} onChange={(e) => setOrigem(e.target.value)}>
              {disponiveis.map((m) => <option key={m} value={m}>{competenciaCurta(m)}</option>)}
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-schmersal-gray-dark dark:text-white cursor-pointer">
            <input type="checkbox" checked={substituir} onChange={(e) => setSubstituir(e.target.checked)} className="rounded border-schmersal-gray-light" />
            Substituir o que já existe em {competenciaCurta(destino)}
          </label>
          <Erro texto={erro} />
          <button
            onClick={copiar} disabled={salvando}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors disabled:opacity-50"
          >
            {salvando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
            Copiar
          </button>
        </>
      )}
    </Moldura>
  );
}

/**
 * Cria a pessoa já sem acesso ao sistema: nasce inativa e com senha aleatória,
 * porque parte do time precisa entrar no rateio antes de ter login.
 */
function ModalNovaPessoa({ onFechar, onSalvo }: { onFechar: () => void; onSalvo: (msg: string) => void }) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [cargo, setCargo] = useState('');
  const [squadId, setSquadId] = useState('');
  const [squads, setSquads] = useState<{ id: string; nome: string }[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const res = await apiService.listarSquads();
      if (res.success) setSquads(res.data as { id: string; nome: string }[]);
    })();
  }, []);

  const criar = async () => {
    if (!nome.trim()) { setErro('Informe o nome'); return; }
    if (!email.trim()) { setErro('Informe o e-mail — é a chave da pessoa no sistema'); return; }
    setSalvando(true);
    // Senha aleatória forte: a pessoa não entra por aqui. Quando ganhar acesso,
    // é ativada e tem a senha redefinida em Usuários.
    const senha = `Ac${crypto.randomUUID().replace(/-/g, '').slice(0, 14)}!9`;
    const res = await apiService.criarUsuario({
      nome: nome.trim(),
      email: email.trim().toLowerCase(),
      senha,
      role: 'user',
      ativo: false,
      cargo: cargo.trim() || undefined,
      squadId: squadId || undefined,
    });
    setSalvando(false);
    if (!res.success) { setErro(res.errors?.[0] || res.message || 'Falha ao criar a pessoa'); return; }
    onSalvo(`${nome.trim()} criada sem acesso ao sistema — já pode ser alocada.`);
  };

  return (
    <Moldura
      titulo="Nova pessoa"
      subtitulo="Nasce sem acesso ao sistema. Serve para entrar no rateio antes de ter login."
      onFechar={onFechar}
    >
      <div>
        <label htmlFor="np-nome" className={classeRotulo}>Nome</label>
        <input id="np-nome" autoFocus className={`${classeCampo} w-full`} value={nome} onChange={(e) => setNome(e.target.value)} />
      </div>
      <div>
        <label htmlFor="np-email" className={classeRotulo}>E-mail</label>
        <input id="np-email" type="email" className={`${classeCampo} w-full`} value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="flex gap-3">
        <div className="flex-1">
          <label htmlFor="np-cargo" className={classeRotulo}>Cargo <span className="font-normal">(opcional)</span></label>
          <input id="np-cargo" className={`${classeCampo} w-full`} value={cargo} onChange={(e) => setCargo(e.target.value)} />
        </div>
        <div className="flex-1">
          <label htmlFor="np-squad" className={classeRotulo}>Squad <span className="font-normal">(opcional)</span></label>
          <div className="relative">
            <select id="np-squad" className={`${classeCampo} w-full appearance-none pr-8`} value={squadId} onChange={(e) => setSquadId(e.target.value)}>
              <option value="">—</option>
              {squads.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-schmersal-gray-medium pointer-events-none" aria-hidden />
          </div>
        </div>
      </div>

      <Erro texto={erro} />

      <button
        onClick={criar} disabled={salvando}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors disabled:opacity-50"
      >
        {salvando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <UserPlus className="h-4 w-4" aria-hidden />}
        Criar pessoa
      </button>
    </Moldura>
  );
}


/**
 * Um bolo de custo do período. O valor total é conhecido; o critério decide
 * como ele desce para os PIs. Remover é destrutivo, então pede confirmação.
 */
function ModalPool({ pool, projetosComAlocacao, onFechar, onSalvo }: {
  pool: PoolCusto | null;
  projetosComAlocacao: { codigo: string; nome: string; cabecas: number }[];
  onFechar: () => void;
  onSalvo: (msg: string) => void;
}) {
  const [nome, setNome] = useState(pool?.nome ?? '');
  const [valor, setValor] = useState(pool ? String(pool.valor) : '');
  const [inicio, setInicio] = useState(pool?.competenciaInicio ?? competenciaAtual());
  const [fim, setFim] = useState(pool?.competenciaFim ?? competenciaAtual());
  const [criterio, setCriterio] = useState<CriterioRateio>(pool?.criterio ?? 'por_cabeca');
  const [escopo, setEscopo] = useState<string[]>(pool?.piCodigos ?? []);
  const [buscaPi, setBuscaPi] = useState('');
  const [opcoesPi, setOpcoesPi] = useState<{ codigo: string; descricao: string | null }[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [confirmandoRemocao, setConfirmandoRemocao] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const meses = useMemo(competenciasProximas, []);

  useEffect(() => {
    if (!buscaPi.trim()) { setOpcoesPi([]); return; }
    const t = setTimeout(async () => {
      const res = await apiService.listarPisCarteira({ busca: buscaPi, limite: 12 });
      if (res.success) setOpcoesPi((res.data as { pis: { codigo: string; descricao: string | null }[] }).pis);
    }, 300);
    return () => clearTimeout(t);
  }, [buscaPi]);

  const alternar = (codigo: string) =>
    setEscopo((atual) => atual.includes(codigo) ? atual.filter((c) => c !== codigo) : [...atual, codigo]);

  const salvar = async () => {
    const v = parseValor(valor);
    if (!nome.trim()) { setErro('Informe o nome do custo'); return; }
    if (v === null) { setErro('Informe o valor'); return; }
    setSalvando(true);
    const res = await apiService.salvarPoolCarteira({
      id: pool?.id, nome: nome.trim(), valor: v,
      competenciaInicio: inicio, competenciaFim: fim, criterio, piCodigos: escopo,
    });
    setSalvando(false);
    if (!res.success) { setErro(res.errors?.[0] || res.message || 'Falha ao salvar'); return; }
    onSalvo(escopo.length
      ? `${nome.trim()} salvo — ${moedaExata(v)} dividido entre ${escopo.length} projeto(s).`
      : `${nome.trim()} salvo — ${moedaExata(v)} rateado entre todos os projetos com alocação.`);
  };

  const remover = async () => {
    if (!pool) return;
    setSalvando(true);
    const res = await apiService.removerPoolCarteira(pool.id);
    setSalvando(false);
    if (!res.success) { setErro(res.message || 'Falha ao remover'); return; }
    onSalvo(`${pool.nome} removido.`);
  };

  return (
    <Moldura
      titulo={pool ? `Editar ${pool.nome}` : 'Novo custo rateado'}
      subtitulo="O valor total do período desce para os PIs pelo critério escolhido."
      onFechar={onFechar}
    >
      <div className="flex gap-3">
        <div className="flex-1">
          <label htmlFor="pl-nome" className={classeRotulo}>Nome</label>
          <input id="pl-nome" autoFocus className={`${classeCampo} w-full`} value={nome}
            onChange={(e) => setNome(e.target.value)} placeholder="Material" />
        </div>
        <div className="flex-1">
          <label htmlFor="pl-valor" className={classeRotulo}>Valor total</label>
          <input id="pl-valor" className={`${classeCampo} w-full`} value={valor}
            onChange={(e) => setValor(e.target.value)} placeholder="1.318.000" />
        </div>
      </div>

      <div className="flex gap-3">
        <div className="flex-1">
          <label htmlFor="pl-ini" className={classeRotulo}>De</label>
          <select id="pl-ini" className={`${classeCampo} w-full`} value={inicio} onChange={(e) => setInicio(e.target.value)}>
            {meses.map((m) => <option key={m} value={m}>{competenciaCurta(m)}</option>)}
          </select>
        </div>
        <div className="flex-1">
          <label htmlFor="pl-fim" className={classeRotulo}>Até</label>
          <select id="pl-fim" className={`${classeCampo} w-full`} value={fim} onChange={(e) => setFim(e.target.value)}>
            {meses.map((m) => <option key={m} value={m}>{competenciaCurta(m)}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="pl-crit" className={classeRotulo}>Critério de rateio</label>
        <select id="pl-crit" className={`${classeCampo} w-full`} value={criterio}
          onChange={(e) => setCriterio(e.target.value as CriterioRateio)}>
          {CRITERIOS.map((c) => <option key={c.id} value={c.id}>{c.rotulo}</option>)}
        </select>
        <p className="mt-1 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
          {CRITERIOS.find((c) => c.id === criterio)?.explicacao}
        </p>
      </div>

      <div>
        <span className={classeRotulo}>Quais projetos dividem este custo</span>
        <div className="flex flex-wrap gap-1.5">
          {/* Nenhum selecionado = todos com alocação. É o caso comum, então é o
              estado inicial e tem um botão explícito para voltar a ele. */}
          <button
            onClick={() => setEscopo([])}
            className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
              escopo.length === 0
                ? 'border-schmersal-blue bg-schmersal-blue text-white'
                : 'border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-gray-dark dark:text-white'
            }`}
          >
            Todos com alocação
          </button>
          {projetosComAlocacao.map((p) => (
            <button
              key={p.codigo}
              onClick={() => alternar(p.codigo)}
              title={`${p.codigo} · ${p.cabecas.toFixed(0)} pessoa-mês`}
              className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                escopo.includes(p.codigo)
                  ? 'border-schmersal-blue bg-schmersal-blue text-white'
                  : 'border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-gray-dark dark:text-white'
              }`}
            >
              {p.nome}
            </button>
          ))}
          {escopo.filter((c) => !projetosComAlocacao.some((p) => p.codigo === c)).map((c) => (
            <button
              key={c}
              onClick={() => alternar(c)}
              className="rounded-full border border-schmersal-blue bg-schmersal-blue text-white px-2.5 py-1 text-xs"
            >
              {c}
            </button>
          ))}
        </div>

        <div className="relative mt-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-schmersal-gray-medium" aria-hidden />
          <input
            className={`${classeCampo} w-full pl-9`}
            value={buscaPi}
            onChange={(e) => setBuscaPi(e.target.value)}
            placeholder="buscar outro PI da carteira"
          />
        </div>
        {opcoesPi.length > 0 && (
          <div className="mt-1 max-h-36 overflow-y-auto border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg divide-y divide-rg-hairline dark:divide-schmersal-gray-dark">
            {opcoesPi.map((o) => (
              <button
                key={o.codigo}
                onClick={() => { alternar(o.codigo); setBuscaPi(''); }}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-schmersal-gray-light/50 dark:hover:bg-schmersal-gray-dark/50"
              >
                <span className="font-medium text-schmersal-gray-dark dark:text-white">{o.codigo}</span>
                <span className="block text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light truncate">
                  {o.descricao}
                </span>
              </button>
            ))}
          </div>
        )}

        <p className="mt-1 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
          {escopo.length === 0
            ? 'Divide entre todos os projetos que tiverem gente alocada no período.'
            : `Só estes ${escopo.length} projeto(s) recebem — mesmo os que não têm ninguém alocado.`}
        </p>
      </div>

      <Erro texto={erro} />

      <button
        onClick={salvar} disabled={salvando}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors disabled:opacity-50"
      >
        {salvando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Check className="h-4 w-4" aria-hidden />}
        Salvar
      </button>

      {pool && (
        confirmandoRemocao ? (
          <div className="flex items-center justify-between gap-3 pt-3 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark">
            <span className="text-sm text-schmersal-gray-dark dark:text-white">Remover {pool.nome} do rateio?</span>
            <span className="flex items-center gap-2">
              <button onClick={remover} disabled={salvando} className="text-sm px-3 py-1.5 rounded-lg bg-schmersal-yellow-dark text-white disabled:opacity-50">
                Remover
              </button>
              <button onClick={() => setConfirmandoRemocao(false)} className="text-sm text-schmersal-gray-medium hover:underline">
                Cancelar
              </button>
            </span>
          </div>
        ) : (
          <button
            onClick={() => setConfirmandoRemocao(true)}
            className="w-full inline-flex items-center justify-center gap-2 pt-3 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark text-sm text-schmersal-gray-medium hover:text-schmersal-yellow-dark"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
            Remover este custo
          </button>
        )
      )}
    </Moldura>
  );
}
